/** @format */

import { animated, useTransition } from "@react-spring/web";
import React, { type FC, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Icon } from "senf-atomic-design-system";
import { getPhaseOfDateRange, useGlobalState } from "senf-shared";
import { geminiRequest } from "senf-shared/utils/geminiRequest";
import i18n from "senf-shared/utils/i18n";
import { OptionsTopics } from "../../../data/OptionsTopics";
import { useAuthModal, useDiscardModal, useIsMobile } from "../../../hooks";
import { cn } from "../../../util/classNameConstructor";
import Box from "../../atoms/box/Box";
import Geocoder from "../../atoms/geocoder/Geocoder";
import Skeleton from "../../atoms/skeleton/Skeleton";
import Typography from "../../atoms/typography/Typography";
import Button from "../../core/button/Button";
import { Combobox } from "../../core/inputs/Combobox";
import { Input } from "../../core/inputs/Input";
import ModalButton from "../../molecules/modalStack/ModalButton";
import Tabs from "../../molecules/tabs/Tabs";
import FormContacts from "../../organisms/modalContents/FormContacts";
import AuthAddDetails from "../../templates/auth/AuthAddDetails";
import MainTemplatesLayout from "../../templates/layout/MainTemplatesLayout";
import Navigation from "./Navigation";
import PostIdeaButton from "./PostIdeaButton";

interface PostIdeaProps {
  ideaMap: any;
  addIdeaOptions: any;
  addIdeaOpen: boolean;
  formik: any;
  statefulMap: any;
  statefulCesiumMap: any;
  editIdea: boolean;
  isSubmitting: boolean;
  handleSubmit: any;
  loading: boolean;
  isOut: boolean;
  handle: any;
  project: any;
  index: number;
  setIndex: any;
  hide: boolean;
  geoDataType: string;
  setGeoDataType: any;
  bbox: any;
  variant: "mainApp" | "dashboard" | null;
}

const PostIdea: FC<PostIdeaProps> = ({
  ideaMap,
  addIdeaOptions,
  addIdeaOpen,
  formik,
  statefulMap,
  statefulCesiumMap,
  editIdea,
  isSubmitting,
  handleSubmit,
  loading,
  isOut,
  handle,
  project,
  index,
  setIndex,
  hide,
  geoDataType,
  setGeoDataType,
  bbox,
  variant,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { displayDiscardModal } = useDiscardModal();
  const [translation] = useGlobalState().translationsState;
  const { handleCheckAuth } = useAuthModal(project);
  const [isGenerating, setIsGenerating] = useState(false);
  const [titleEditedManually, setTitleEditedManually] = useState(false);
  const titleGenAbortRef = useRef<AbortController | null>(null);
  const bodyIdleTimerRef = useRef<number | null>(null);
  const titleGenSeqRef = useRef(0);
  const activeRequestSeqRef = useRef<number | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>(300);
  const [titleInputHeight, setTitleInputHeight] = useState<number>(40);
  const prevIndexRef = useRef(index);
  const isGoingForward = index > prevIndexRef.current;

  // Measure title input height when it's available
  useEffect(() => {
    if (titleInputRef.current) {
      const height = titleInputRef.current.offsetHeight;
      setTitleInputHeight(height);
    }
  }, [isEditingTitle, formik?.values.title]);

  // React Spring transitions for each step with direction-aware animations
  const transition0 = useTransition(index === 0, {
    from: {
      transform: isGoingForward
        ? "translate3d(20px, 0px, 0px)"
        : "translate3d(-20px, 0px, 0px)",
      opacity: 0,
    },
    leave: {
      transform: isGoingForward
        ? "translate3d(-20px, 0px, 0px)"
        : "translate3d(20px, 0px, 0px)",
      opacity: 0,
    },
    enter: { transform: "translate3d(0px, 0px, 0px)", opacity: 1 },
    config: { tension: 400, friction: 30 },
  });

  const transition1 = useTransition(index === 1, {
    from: {
      transform: isGoingForward
        ? "translate3d(20px, 0px, 0px)"
        : "translate3d(-20px, 0px, 0px)",
      opacity: 0,
    },
    leave: {
      transform: isGoingForward
        ? "translate3d(-20px, 0px, 0px)"
        : "translate3d(20px, 0px, 0px)",
      opacity: 0,
    },
    enter: { transform: "translate3d(0px, 0px, 0px)", opacity: 1 },
    config: { tension: 400, friction: 30 },
  });

  const transition2 = useTransition(index === 2, {
    from: {
      transform: isGoingForward
        ? "translate3d(20px, 0px, 0px)"
        : "translate3d(-20px, 0px, 0px)",
      opacity: 0,
    },
    leave: {
      transform: isGoingForward
        ? "translate3d(-20px, 0px, 0px)"
        : "translate3d(20px, 0px, 0px)",
      opacity: 0,
    },
    enter: { transform: "translate3d(0px, 0px, 0px)", opacity: 1 },
    config: { tension: 400, friction: 30 },
  });

  const scheduleTitleSuggestion = (desc: string) => {
    try {
      const rawDesc = desc ?? "";
      // Clear pending timers
      if (bodyIdleTimerRef.current) {
        window.clearTimeout(bodyIdleTimerRef.current);
      }

      // If a generation is in progress and the user typed again, abort now
      if (isGenerating && titleGenAbortRef.current) {
        try {
          titleGenAbortRef.current.abort();
        } catch {}
        // Do not reset isGenerating here so the skeleton stays visible.
      }

      // Only suggest if user hasn't edited the title manually
      if (titleEditedManually) {
        return;
      }

      const trimmed = rawDesc.trim();
      // Allow generation on shorter texts to kick in earlier
      if (!trimmed || trimmed.length < 8) {
        return;
      }

      // bump sequence for this scheduled run
      titleGenSeqRef.current += 1;
      const mySeq = titleGenSeqRef.current;

      bodyIdleTimerRef.current = window.setTimeout(async () => {
        // this timer is now executing; clear the pending reference
        bodyIdleTimerRef.current = null;
        let localController: AbortController | null = null;
        try {
          setIsGenerating(true);
          // Abort any in-flight request
          if (titleGenAbortRef.current) {
            titleGenAbortRef.current.abort();
          }
          const controller = new AbortController();
          localController = controller;
          titleGenAbortRef.current = controller;
          activeRequestSeqRef.current = mySeq;

          // Prepare topics for the assistant
          const topics = optionsTopics.map((topic) => ({
            id: topic.value,
            label: translation?.[topic.label] || topic.label,
            color: topic.color,
          }));

          const prompt = `POST CONTENT:\n${trimmed}\n\nAVAILABLE TOPICS:\n${JSON.stringify(topics, null, 2)}`;

          const result = await geminiRequest({
            body: prompt,
            assistantId: "postEnhancer",
            lang: i18n.resolvedLanguage || "de",
            signal: controller.signal,
          });

          if (result && !titleEditedManually) {
            try {
              const response =
                typeof result === "string" ? JSON.parse(result) : result;
              if (response.title) {
                formik?.setFieldValue("title", response.title);
              }
              if (
                response.topicId &&
                response.topicId.trim() !== "" &&
                !formik?.values.topic
              ) {
                formik?.setFieldValue("topic", response.topicId);
              }
              // Small delay to ensure values are set, then validate
              setTimeout(() => {
                formik?.setFieldTouched("title", true);
                formik?.setFieldTouched("topic", true);
              }, 100);
            } catch (parseErr) {
              console.debug("[TitleGen] parse error:", parseErr);
            }
          }
        } catch (err) {
          console.debug("[TitleGen] error", err);
        } finally {
          // Only clear generating state if this is the latest finished request,
          // no new idle timer is pending, and this request wasn't aborted
          const isLatest = activeRequestSeqRef.current === mySeq;
          const hasPendingTimer = !!bodyIdleTimerRef.current;
          const wasAborted = !!localController?.signal.aborted;
          if (isLatest && !hasPendingTimer && !wasAborted) {
            setIsGenerating(false);
          }
        }
      }, 1200);
    } catch (err) {
      console.debug("[TitleGen] schedule error", err);
    }
  };

  const [open, setOpen] = useState<string | boolean | null>("bottom");
  const [shouldSnapToTop, setShouldSnapToTop] = useState(false);
  const innerHeight = useRef(window.innerHeight);
  useEffect(() => {
    if (index === 1) {
      setOpen("top");
      setShouldSnapToTop(true);
      setTimeout(() => setShouldSnapToTop(false), 0);
    }

    // Update previous index after a small delay to ensure transition direction is captured
    const timeoutId = setTimeout(() => {
      prevIndexRef.current = index;
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [index]);

  // Dynamically compute the "top" snap position based on current content height
  const [topSnap, setTopSnap] = useState<number>(innerHeight.current - 78);
  const section0Ref = useRef<HTMLDivElement | null>(null);
  const section1Ref = useRef<HTMLDivElement | null>(null);
  const section2Ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const measure = () => {
      const currentEl =
        index === 0
          ? section0Ref.current
          : index === 1
            ? section1Ref.current
            : section2Ref.current;
      const contentHeight =
        currentEl?.scrollHeight ||
        currentEl?.getBoundingClientRect()?.height ||
        0;
      const padding = 24; // account for internal paddings/margins
      const desiredHeight = Math.max(260, Math.ceil(contentHeight + padding));
      const maxHeight = Math.max(260, window.innerHeight - 20);
      setTopSnap(Math.min(desiredHeight, maxHeight));

      // Update container height for animations
      if (index === 0) {
        setContainerHeight(190);
      } else {
        if (isMobile) {
          setContainerHeight(Math.max(400, contentHeight + padding));
        } else {
          setContainerHeight(Math.max(100, contentHeight + padding));
        }
      }
    };

    // Small delay to ensure content is rendered
    const timeoutId = setTimeout(measure, 100);

    const RO = (window as any).ResizeObserver as
      | typeof ResizeObserver
      | undefined;
    const observer = RO ? new RO(() => measure()) : undefined;
    const currentEl =
      index === 0
        ? section0Ref.current
        : index === 1
          ? section1Ref.current
          : section2Ref.current;
    if (observer && currentEl) observer.observe(currentEl);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(timeoutId);
      if (observer && currentEl) observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [index]);

  const btnConditions = [
    {
      condition: !formik?.values?.address && geoDataType === "location",
      notice: t("dialogMap:add.locationRequired"),
    },
    {
      condition: isOut && geoDataType === "location",
      notice: t("dialogMap:add.outOfProjectArea"),
    },
  ];

  const resultCondition = btnConditions.find((item) => item.condition);
  const disabled = !!resultCondition;
  const disabledNotice = resultCondition
    ? resultCondition.notice
    : t("dialogMap:add.unknownError");

  const optionsTopics = OptionsTopics(
    ideaMap?.customTopics?.length && ideaMap?.customTopics,
  );

  const [bottomPositionReset, setBottomPositionReset] = useState<
    string | boolean | null
  >("bottom");
  useEffect(() => {
    if (index === 1) {
      setBottomPositionReset(innerHeight.current - 90);
      setTimeout(() => {
        setBottomPositionReset(190);
        document.getElementById("body")?.focus();
      }, 1000);
    } else if (index === 2) {
      setTimeout(() => {
        setBottomPositionReset(innerHeight.current - 90);
        setBottomPositionReset(190);
      }, 1000);
    } else {
      setBottomPositionReset(innerHeight.current - 90);
    }
  }, [index]);

  // Focus management - focus on close button when component opens
  useEffect(() => {
    if (addIdeaOpen && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [addIdeaOpen]);

  return (
    <React.Fragment>
      {isMobile &&
        addIdeaOpen &&
        !hide &&
        geoDataType === "location" &&
        index === 0 && (
          <div className="absolute top-0 p-2.5 z-[99] w-screen">
            <Geocoder
              isCesium={ideaMap?.map?.mapStyle === "cesium"}
              statefulMap={statefulMap}
              statefulCesiumMap={statefulCesiumMap}
              classNames={{
                inputContainer: "bg-surface-default",
              }}
              bbox={bbox}
              value={formik.values.address}
              setValue={(value) => {
                formik.setFieldValue("address", value);
              }}
            />
          </div>
        )}
      {isMobile && index !== 0 && (
        <div
          className="h-[90px] w-full fixed top-0 z-10"
          onClick={() => setIndex(0)}
        />
      )}
      <MainTemplatesLayout
        // header={
        //   editIdea
        //     ? t("util:edit", { val: t("global:post") })
        //     : t("util:compose", { val: t("global:post") })
        // }
        closeButtonVariant="shapeless"
        showNav={false}
        // showNav={index === 0 ? true : false}
        handleButtonClose={() =>
          displayDiscardModal({
            title: t("dialogMap:add.discardTitle"),
            description: t("dialogMap:add.discardDescription"),
            submitText: t("dialogMap:add.discardConfirmButton"),
            onSubmit: async () => {
              formik?.resetForm();
              handle.openAddIdea(false);
            },
          })
        }
        // footer={
        //   <div className="sticky bottom-0 w-full h-12">
        //     <Navigation
        //       index={index}
        //       setIndex={setIndex}
        //       formik={formik}
        //       isSubmitting={isSubmitting}
        //       handleSubmit={handleSubmit}
        //       loading={loading}
        //       editIdea={editIdea}
        //       isOut={isOut}
        //       project={project}
        //       geoDataType={geoDataType}
        //       isGenerating={isGenerating}
        //       titleEditedManually={titleEditedManually}
        //     />{" "}
        //   </div>
        // }
        options={{
          openTo: index === 0 ? "bottom" : "top", // bottom only works if defined in options
          open,
          radius: 20,
          // onChange: setPosition,
          positions: {
            top: index === 0 ? 190 : innerHeight.current - 20,
            middle: index === 0 ? 190 : innerHeight.current - 20,
            bottom: 190,
          },
        }}
        onPositionChange={(position) => {
          console.log("position", position);
          if ((index === 1 || index === 2) && position === "draggingDown") {
            console.log("draggingDown");
            setIndex(0);
          }
        }}
        shouldSnapToTop={shouldSnapToTop}
        closeButtonTooltipLabel={t("dialogMap:add.discardTitle")}
      >
        <div className="relative px-5">
          <div
            style={{
              position: "relative",
              minHeight: `${containerHeight}px`,
              transition: "min-height 0.3s ease-out",
            }}
          >
            {transition0((style, item) =>
              item ? (
                <animated.div
                  style={{
                    ...style,
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    width: "100%",
                  }}
                >
                  <div ref={section0Ref}>
                    <div
                      className={cn(
                        "flex items-center justify-between gap-5 py-3 -mr-2",
                        isMobile && "pt-5",
                      )}
                    >
                      <h1 className="heading-lg">
                        {editIdea
                          ? t("util:edit", { val: t("global:post") })
                          : t("util:new_male", { val: t("global:post") })}
                      </h1>
                      <Button
                        ref={closeButtonRef}
                        size="md"
                        variant="shapeless"
                        leadingIcon="x-md"
                        classNameWrapper="ml-[10px]"
                        onClick={() =>
                          displayDiscardModal({
                            title: t("dialogMap:add.discardTitle"),
                            description: t("dialogMap:add.discardDescription"),
                            submitText: t("dialogMap:add.discardConfirmButton"),
                            onSubmit: async () => {
                              formik?.resetForm();
                              handle.openAddIdea(false);
                            },
                          })
                        }
                      />
                    </div>
                    <Box
                      display="flex"
                      gap="16px"
                      flexDirection="column"
                      width="100%"
                      height="auto"
                      marginBottom="16px"
                    >
                      {(ideaMap?.addIdeaOptions?.generalIdeas ||
                        ideaMap?.addIdeaOptions?.drawPath ||
                        ideaMap?.addIdeaOptions?.drawArea) && (
                        <Tabs
                          type="filled"
                          classNames={{
                            wrapper: "h-11 max-w-full",
                          }}
                          hideTabText={
                            ideaMap?.addIdeaOptions?.drawPath ||
                            ideaMap?.addIdeaOptions?.drawArea
                          }
                          active={geoDataType}
                          setActive={setGeoDataType}
                          tabs={addIdeaOptions}
                        />
                      )}
                      {!isMobile && geoDataType === "location" && (
                        <Geocoder
                          isCesium={ideaMap?.map?.mapStyle === "cesium"}
                          statefulMap={statefulMap}
                          statefulCesiumMap={statefulCesiumMap}
                          bbox={bbox}
                          value={formik.values.address}
                          setValue={(value) => {
                            formik.setFieldValue("address", value);
                          }}
                        />
                      )}
                    </Box>

                    {!ideaMap?.interactionOptions?.allowPosts &&
                    variant === "mainApp" ? (
                      <Button
                        disabled={true}
                        size="lg"
                        text={t("dialogMap:add.disabled")}
                        className="w-full"
                      />
                    ) : (!ideaMap?.interactionOptions?.allowPosts &&
                        variant !== "mainApp") ||
                      getPhaseOfDateRange(
                        ideaMap?.startDate,
                        ideaMap?.endDate,
                      ) === "while" ? (
                      <Button
                        trackId="addIdeaPage_continueClick"
                        variant="primary"
                        width="max"
                        size="lg"
                        text={t("global:next")}
                        onClick={() => setIndex(1)}
                        // state={disabled ? "disabled" : "default"} // REMOVE THIS LATER ON
                        disabled={disabled}
                        disabledNotice={disabledNotice}
                      />
                    ) : (
                      <PostIdeaButton ideaMap={ideaMap} handle={handle} />
                    )}
                  </div>
                </animated.div>
              ) : null,
            )}
            {transition1((style, item) =>
              item ? (
                <animated.div
                  style={{
                    ...style,
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    width: "100%",
                  }}
                >
                  <div
                    ref={section1Ref}
                    className="flex flex-col gap-3 relative w-full"
                  >
                    <div
                      className={cn("h-full w-full pt-4", isMobile && "pt-0")}
                    >
                      {isEditingTitle ||
                      (!isGenerating &&
                        formik?.values.title?.trim()?.length) ? (
                        <div className="flex-1">
                          <Input
                            name="title"
                            type="textarea"
                            className="heading-lg"
                            rows={1}
                            placeholder={t("util:new_male", {
                              val: t("global:post"),
                            })}
                            ref={titleInputRef as any}
                            onChange={(e) => {
                              setTitleEditedManually(true);
                              formik?.handleChange(e);
                            }}
                            onBlur={() => {
                              setIsEditingTitle(false);
                            }}
                            value={formik?.values.title}
                            error={
                              formik?.touched.title &&
                              Boolean(formik?.errors?.title)
                            }
                            helper={
                              formik?.touched.title &&
                              (formik?.errors?.title as string)
                            }
                            onFocus={() =>
                              handleCheckAuth({ checkAuthAddDetails: false })
                            }
                          />
                        </div>
                      ) : (
                        <div className="flex w-full items-center">
                          <h1 className="heading-lg w-full">
                            {isGenerating &&
                            formik?.values.title?.trim()?.length ? (
                              <Skeleton
                                height={titleInputHeight}
                                className="w-full !my-0"
                                classNames={{
                                  item: "rounded-xl !my-0",
                                }}
                              />
                            ) : formik?.values.title?.trim()?.length ? (
                              formik.values.title
                            ) : (
                              t("util:new_male", { val: t("global:post") })
                            )}
                          </h1>
                          {!isGenerating &&
                          formik?.values.title?.trim()?.length ? (
                            <Button
                              size="md"
                              variant="shapeless"
                              leadingIcon="edit-alt"
                              classNameWrapper="ml-[10px]"
                              onClick={() => {
                                setIsEditingTitle(true);
                                setTimeout(
                                  () => titleInputRef.current?.focus(),
                                  0,
                                );
                              }}
                            />
                          ) : null}
                        </div>
                      )}
                    </div>
                    <Input
                      name="body"
                      type="textarea"
                      placeholder={t(
                        "dialogMap:add.enterIdeaDescriptionPlaceholder",
                      )}
                      rows={5}
                      maxRows={15}
                      onChange={(e) => {
                        formik?.handleChange(e);
                        scheduleTitleSuggestion(
                          (e.target as HTMLTextAreaElement).value,
                        );
                      }}
                      onBlur={formik?.handleBlur}
                      value={formik?.values.body}
                      error={
                        formik?.touched.body && Boolean(formik?.errors?.body)
                      }
                      helper={formik?.touched.body && formik?.errors?.body}
                      onFocus={() =>
                        handleCheckAuth({ checkAuthAddDetails: false })
                      }
                      minLength={
                        ideaMap?.addIdeaOptions?.description?.minChar ?? 50
                      }
                      maxLength={
                        ideaMap?.addIdeaOptions?.description?.maxChar ?? 800
                      }
                    />

                    <Combobox
                      enableSearch={false}
                      asModal
                      value={
                        translation?.[
                          ideaMap?.customTopicsObject?.[formik.values.topic]
                            ?.label
                        ] ||
                        ideaMap?.customTopicsObject?.[formik.values.topic]
                          ?.label
                      }
                      placeholder={t("dialogMap:add.chooseCategoryPlaceholder")}
                      onChange={(option) => {
                        formik?.setFieldValue("topic", option.value);
                      }}
                      leadingIcon={
                        <Icon
                          name={
                            ideaMap?.customTopicsObject?.[formik.values.topic]
                              ?.color
                              ? "point-md"
                              : "ideaCategory/all"
                          }
                          color={
                            ideaMap?.customTopicsObject?.[formik.values.topic]
                              ?.color
                          }
                          className={
                            !ideaMap?.customTopicsObject?.[formik.values.topic]
                              ?.color
                              ? "scale-[60%]"
                              : ""
                          }
                        />
                      }
                      options={optionsTopics.map(({ value, label, color }) => ({
                        value: value,
                        label: translation?.[label] || label,
                        leadingIcon: (
                          <Icon
                            name="point-md"
                            style={{ "--color": color } as any}
                            color={color}
                          />
                        ),
                      }))}
                      classNames={{
                        optionLeadingIcon:
                          "text-[var(--color)] group-hover/option:text-[var(--color)]",
                      }}
                    />

                    {ideaMap?.addIdeaOptions?.contactFieldsAllowed && (
                      <React.Fragment>
                        <Box gap="4px" flexDirection="column">
                          <Typography variant="bodySm">
                            {t("global:optional")}
                          </Typography>
                          <Box
                            gap="10px"
                            flexDirection="column"
                            marginTop="5px"
                          >
                            <ModalButton
                              width="max"
                              variant="basic"
                              text={
                                formik?.values.weblinkTitle ||
                                formik?.values.weblink ||
                                t("util:add", { val: t("global:link") })
                              }
                              leadingIcon="link"
                              trailingIcon="plus-md"
                              options={{
                                title: t("util:add", { val: t("global:link") }),
                                id: "addWeblink",
                                cancelText: t("global:cancel"),
                                submitText: t("global:save"),
                                zIndex: 99999,
                              }}
                            >
                              <Box marginInline="20px" flexDirection="column">
                                <FormContacts
                                  formik={formik}
                                  inputData={[
                                    {
                                      name: "weblinkTitle",
                                      placeholder:
                                        "dialogMap:add.addWeblinkTitlePlaceholder",
                                    },
                                    {
                                      name: "weblink",
                                      placeholder:
                                        "dialogMap:add.addWeblinkPlaceholder",
                                    },
                                  ]}
                                  header=""
                                />
                              </Box>
                            </ModalButton>

                            <ModalButton
                              variant="basic"
                              width="max"
                              text={
                                formik?.values.contactTitle ||
                                formik?.values.contact ||
                                t("util:add", { val: t("global:emailAddress") })
                              }
                              leadingIcon="mail-fill"
                              trailingIcon="plus-md"
                              options={{
                                title: t("util:add", {
                                  val: t("global:emailAddress"),
                                }),
                                id: "addContact",
                                cancelText: t("global:cancel"),
                                submitText: t("global:save"),
                                zIndex: 99999,
                              }}
                            >
                              <Box marginInline="20px" flexDirection="column">
                                <FormContacts
                                  formik={formik}
                                  inputData={[
                                    {
                                      name: "contactTitle",
                                      placeholder:
                                        "dialogMap:add.addEmailTitlePlaceholder",
                                    },
                                    {
                                      name: "contact",
                                      placeholder:
                                        "dialogMap:add.addEmailPlaceholder",
                                    },
                                  ]}
                                  header={t(
                                    "dialogMap:add.addEmailDescription",
                                  )}
                                />
                              </Box>
                            </ModalButton>
                          </Box>
                        </Box>
                      </React.Fragment>
                    )}

                    <span className="body-sm pb-16 text-text-subdued">
                      <Trans i18nKey="auth:privacyStatement">
                        ...
                        <Link
                          to={`/projects/${project?.projectId}/privacyPolicy`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            textDecoration: "underline",
                            fontWeight: "500",
                          }}
                        />
                        ...
                      </Trans>
                    </span>
                  </div>
                </animated.div>
              ) : null,
            )}
            {transition2((style, item) =>
              item ? (
                <animated.div
                  style={{
                    ...style,
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    width: "100%",
                  }}
                >
                  <div
                    ref={section2Ref}
                    className="flex flex-col gap-5 relative w-full"
                  >
                    <AuthAddDetails
                      variant="post"
                      project={project}
                      onPrev={() => {
                        setIndex(1);
                      }}
                      onAfterSave={() => {
                        handleSubmit();
                      }}
                    />
                  </div>
                </animated.div>
              ) : null,
            )}
          </div>
          {index === 1 && (
            <Navigation
              index={index}
              setIndex={setIndex}
              formik={formik}
              isSubmitting={isSubmitting}
              handleSubmit={handleSubmit}
              loading={loading}
              editIdea={editIdea}
              isOut={isOut}
              project={project}
              geoDataType={geoDataType}
              isGenerating={isGenerating}
              titleEditedManually={titleEditedManually}
            />
          )}
        </div>
      </MainTemplatesLayout>
    </React.Fragment>
  );
};

export default PostIdea;
